// @ts-check
import { paintQuantum } from './quantum.js';
import { paintAtmosphere, paintGrid, paintMachinery, paintSignals } from './boot-sector.js';
import { paintWorldMachinery, paintWorldSignals } from './world-machinery.js';
/** @typedef {import('./boot-sector.js').Palette} Palette */
/** @typedef {{width:number,height:number,floor:number}} View */

/** Bounded visible scenery, stable when revisiting negative or positive coordinates.
 * @param {number} cameraX @param {number} depth @param {number} tileWidth @param {number} width
 */
export function visibleTiles(cameraX, depth, tileWidth, width) {
  const offset = cameraX * depth, first = Math.floor(offset / tileWidth) - 1;
  return Array.from({ length: Math.ceil(width / tileWidth) + 3 }, (_, step) => {
    const index = first + step;
    return { index, x: index * tileWidth - offset };
  });
}

const LAYERS = [{ depth: 0.16, width: 960, height: 0.84, max: 620 }, { depth: 0.48, width: 760, height: 0.58, max: 440 }];

/** Browser resource owner. Static machinery is cached in bounded tiles; sparse
 * signal traffic uses the existing game clock. No independent animation loop.
 * @param {HTMLCanvasElement} canvas
 */
export function createWorldRenderer(canvas) {
  const context = canvas.getContext('2d');
  const style = window.getComputedStyle(canvas);
  const color = (/** @type {string} */ name) => style.getPropertyValue(`--color-${name}`).trim();
  const colors = {
    void: color('space-void'), haze: color('space-haze'), tower: color('space-tower'),
    floor: color('space-floor'), wire: color('space-wire'), cyan: color('glow-cyan'),
    violet: color('space-violet'), light: color('surface-12'), metal: color('metal'), steel: color('steel'),
  };
  /** @type {Map<string,HTMLCanvasElement>} */ const cache = new Map();
  const atmosphere = canvas.ownerDocument.createElement('canvas');
  let view = { width: 1, height: 1, floor: 1 }, ratio = 1, previous = '';
  let time = 0, energy = 0, disposed = false, reduced = false;
  let paints = 0, builds = 0;
  let world = 1;
  const base = { ...colors };
  function clearCache() {
    for (const tile of cache.values()) tile.width = tile.height = 1;
    cache.clear();
  }
  /** @param {number} layer @param {number} index @param {number} height */
  function tileFor(layer, index, height) {
    const key = `${layer}:${index}`;
    let tile = cache.get(key);
    if (!tile) {
      // An ultrawide display still has a bounded cache and transient allocation.
      if (cache.size >= 12) {
        const first = cache.keys().next().value;
        if (first !== undefined) { const old = cache.get(first); if (old) old.width = old.height = 1; cache.delete(first); }
      }
      tile = canvas.ownerDocument.createElement('canvas');
      const density = Math.min(ratio, 1.25);
      tile.width = Math.ceil(LAYERS[layer].width * density); tile.height = Math.ceil(height * density);
      const ctx = tile.getContext('2d');
      if (ctx) { ctx.setTransform(density, 0, 0, density, 0, 0); if (world === 1) paintMachinery(ctx, index, LAYERS[layer].width, height, layer, colors); else paintWorldMachinery(ctx, world, index, LAYERS[layer].width, height, layer, colors); }
      cache.set(key, tile); builds++;
    }
    return tile;
  }
  return {
    /** @param {number} next */
    setWorld(next) {
      if (next === world || disposed) return;
      world = next; Object.assign(colors, base);
      colors.cyan = color(`world-${world}-primary`) || base.cyan;
      colors.violet = color(`world-${world}-accent`) || base.violet;
      for (const name of ['metal', 'steel', 'wire']) colors[/** @type {'metal'|'steel'|'wire'} */ (name)] = color(`world-${world}-${name}`) || base[/** @type {'metal'|'steel'|'wire'} */ (name)];
      clearCache(); previous = '';
    },
    /** @param {import('../game/types.js').GameEvent} event */
    accept(event) {
      if (disposed) return;
      if (event.type === 'restarted') { time = energy = 0; previous = ''; }
      if (['block-activated', 'block-broken', 'pickup', 'uplink-entered', 'boss-hit', 'boss-defeated', 'signal'].includes(event.type)) energy = ['uplink-entered', 'boss-defeated'].includes(event.type) ? 1 : 0.75;
    },
    /** @param {number} dt */
    step(dt) {
      if (disposed || reduced) return;
      time += dt; energy = Math.max(0, energy - dt * 0.6);
    },
    /** @param {View} next */
    resize(next) {
      if (disposed) return;
      view = next; ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.round(view.width * ratio)); canvas.height = Math.max(1, Math.round(view.height * ratio));
      atmosphere.width = canvas.width; atmosphere.height = canvas.height;
      const ctx = atmosphere.getContext('2d');
      if (ctx) { ctx.setTransform(ratio, 0, 0, ratio, 0, 0); paintAtmosphere(ctx, view.width, view.height, colors); }
      clearCache(); previous = '';
    },
    /** @param {number} cameraX @param {boolean} reducedMotion @param {number} [vertical] */
    render(cameraX, reducedMotion, vertical = 0) {
      if (!context || disposed) return;
      if (reducedMotion !== reduced) energy = 0;
      reduced = reducedMotion;
      const tick = reduced ? 0 : Math.floor(time * 30);
      const key = `${cameraX}:${vertical}:${reduced}:${tick}:${reduced ? 0 : Math.round(energy * 30)}`;
      if (key === previous) return;
      const clock = tick / 30, distant = reduced ? 0 : cameraX;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.drawImage(atmosphere, 0, 0, view.width, view.height);
      if (world === 9) paintQuantum(context, view, cameraX, clock, colors, reduced);
      else LAYERS.forEach((layer, i) => {
        const height = Math.min(layer.max, Math.max(180, view.height * layer.height));
        const y = view.floor - 62 - height + vertical * layer.depth;
        for (const tile of visibleTiles(distant, layer.depth, layer.width, view.width)) {
          if (tile.x + layer.width <= 0 || tile.x >= view.width) continue;
          context.globalAlpha = i === 0 ? .68 : .94;
          context.drawImage(tileFor(i, tile.index, height), tile.x, y, layer.width, height);
          context.globalAlpha = 1;
          if (i === 0) {
            context.save(); context.translate(tile.x, y);
            if (world === 1) paintSignals(context, tile.index, layer.width, height, clock, reduced ? 0 : energy, colors);
            else paintWorldSignals(context, world, tile.index, layer.width, height, clock, reduced ? 0 : energy, colors);
            context.restore();
          }
        }
      });
      if (world !== 9) paintGrid(context, view.width, view.height, view.floor + vertical, cameraX, colors);
      // A quiet band behind the action keeps small bots and QP's letters legible.
      const shade = context.createLinearGradient(0, view.floor - 190, 0, view.floor);
      shade.addColorStop(0, 'transparent'); shade.addColorStop(1, colors.floor);
      context.fillStyle = shade; context.globalAlpha = 0.36;
      context.fillRect(0, view.floor - 190, view.width, 190); context.globalAlpha = 1;
      canvas.dataset.cameraX = String(cameraX); canvas.dataset.worldTime = String(tick);
      canvas.dataset.energy = String(reduced ? 0 : energy);
      canvas.dataset.theme = ['boot-sector', 'packet-highway', 'memory-banks', 'clock-domain', 'render-grid', 'thermal-core', 'security-layer', 'kernel', 'quantum-space'][world - 1];
      paints++; previous = key;
    },
    get active() { return world === 9 && !reduced && !disposed; },
    get stats() { return { paints, builds, tiles: cache.size }; },
    dispose() {
      if (disposed) return;
      disposed = true; clearCache(); atmosphere.width = atmosphere.height = 1;
    },
  };
}
